/**
 * @fileoverview Lógica de evaluación de features
 * @module utils/featureEvaluation
 */

import { serverTimestamp } from 'firebase/firestore';
import { 
  FeatureMetrics, 
  FeatureState, 
  FamilyFeatures,
  DEFAULT_FEATURE_STATE 
} from '../types/features';

/**
 * Evalúa todas las features y retorna sus estados
 */
export function evaluateAllFeatures(
  savedFeatures: any,
  metrics: FeatureMetrics
): FamilyFeatures {
  
  const result = {
    advancedTasks: evaluateAdvancedTasks(savedFeatures?.advancedTasks, metrics),
    advancedActivities: evaluateAdvancedActivities(savedFeatures?.advancedActivities, metrics),
    dashboardMode: evaluateDashboardMode(savedFeatures?.dashboardMode),
    templateLibrary: evaluateTemplateLibrary(savedFeatures?.templateLibrary, metrics),
    calendar: evaluateCalendar(savedFeatures?.calendar, metrics),
    groupGoals: evaluateGroupGoals(savedFeatures?.groupGoals, metrics),
    advancedRecipes: evaluateAdvancedRecipes(savedFeatures?.advancedRecipes, metrics),
    mealPlanCalendar: evaluateMealPlanCalendar(savedFeatures?.mealPlanCalendar, metrics),
    shoppingList: evaluateShoppingList(savedFeatures?.shoppingList, metrics, savedFeatures),
    gritiAI: evaluateGritiAI(savedFeatures?.gritiAI, metrics)
  };
  
  return result;
}

/**
 * Tareas Avanzadas: 5+ creadas, 10+ completadas, 3+ días activo
 */
function evaluateAdvancedTasks(saved: any, metrics: FeatureMetrics): FeatureState {
  const conditionMet = 
    metrics.tasksCreated >= 5 &&
    metrics.tasksCompleted >= 10 &&
    metrics.daysActive >= 3;
  
  return createFeatureState(saved, conditionMet);
}

/**
 * Actividades Avanzadas: 3+ creadas, 5+ días activo
 */
function evaluateAdvancedActivities(saved: any, metrics: FeatureMetrics): FeatureState {
  const conditionMet = 
    metrics.activitiesCreated >= 3 &&
    metrics.daysActive >= 5;
  
  return createFeatureState(saved, conditionMet);
}

/**
 * Dashboard Avanzado: desbloqueado por defecto, el usuario decide si lo habilita
 */
function evaluateDashboardMode(saved: any): FeatureState {
  return {
    unlocked: true,
    enabled: saved && typeof saved.enabled === 'boolean' ? Boolean(saved.enabled) : false,
    unlockedAt: saved?.unlockedAt || null,
    unlockedBy: saved?.unlockedBy || 'default',
    notificationShown: saved?.notificationShown || false
  };
}

/**
 * Biblioteca Templates: 5+ tareas/actividades creadas, 7+ días activo
 */
function evaluateTemplateLibrary(saved: any, metrics: FeatureMetrics): FeatureState {
  const conditionMet = 
    (metrics.tasksCreated + metrics.activitiesCreated) >= 5 &&
    metrics.daysActive >= 7;
  
  return createFeatureState(saved, conditionMet);
}

/**
 * Calendario: 14+ días activo
 */
function evaluateCalendar(saved: any, metrics: FeatureMetrics): FeatureState {
  const conditionMet = metrics.daysActive >= 14;
  
  return createFeatureState(saved, conditionMet);
}

/**
 * Metas Grupales: 3+ integrantes, 50+ completadas, 10+ días activo
 */
function evaluateGroupGoals(saved: any, metrics: FeatureMetrics): FeatureState {
  const conditionMet = 
    metrics.membersCount >= 3 &&
    metrics.totalCompletions >= 50 &&
    metrics.daysActive >= 10;
  
  return createFeatureState(saved, conditionMet);
}

/**
 * Recetas Avanzadas: 5+ recetas, 7+ días usando recetas, 14+ días activo
 */
function evaluateAdvancedRecipes(saved: any, metrics: FeatureMetrics): FeatureState {
  const conditionMet = 
    metrics.recipesCreated >= 5 &&
    metrics.recipeDaysUsed >= 7 &&
    metrics.daysActive >= 14;
  
  return createFeatureState(saved, conditionMet);
}

/**
 * Calendario de Comidas: 5+ recetas creadas (DEPENDIENTE)
 */
function evaluateMealPlanCalendar(saved: any, metrics: FeatureMetrics): FeatureState {
  const conditionMet = metrics.recipesCreated >= 5;
  
  return createFeatureState(saved, conditionMet);
}

/**
 * Lista de Compras: Calendario de Comidas desbloqueado (DEPENDIENTE)
 */
function evaluateShoppingList(saved: any, metrics: FeatureMetrics, allFeatures: any): FeatureState {
  // Requiere que mealPlanCalendar esté desbloqueado
  const conditionMet = allFeatures?.mealPlanCalendar?.unlocked === true;
  
  return createFeatureState(saved, conditionMet);
}

/**
 * Griti IA: 7+ días activo, 20+ completadas, 3+ secciones exploradas
 */
function evaluateGritiAI(saved: any, metrics: FeatureMetrics): FeatureState {
  const conditionMet = 
    metrics.daysActive >= 7 &&
    metrics.totalCompletions >= 20 &&
    metrics.hasExploredSections;
  
  return createFeatureState(saved, conditionMet);
}

/**
 * Crea un FeatureState a partir de datos guardados y condición
 */
function createFeatureState(saved: any, conditionMet: boolean): FeatureState {
  // Si ya estaba desbloqueado manualmente, mantener ese estado
  if (saved?.unlocked) {
    return {
      unlocked: true,
      enabled: saved.enabled ?? true, // Default true
      unlockedAt: saved.unlockedAt || null,
      unlockedBy: saved.unlockedBy || 'manual',
      notificationShown: saved.notificationShown || false
    };
  }
  
  // Si cumple condiciones automáticas
  if (conditionMet) {
    return {
      unlocked: true,
      enabled: saved?.enabled ?? true, // Respeta preferencia si existe
      unlockedAt: saved?.unlockedAt || null, // Se actualizará al guardar
      unlockedBy: 'auto',
      notificationShown: saved?.notificationShown || false
    };
  }
  
  // Aún bloqueado
  return {
    ...DEFAULT_FEATURE_STATE,
    enabled: saved?.enabled ?? true // Respeta preferencia aunque esté bloqueado
  };
}

/**
 * Identifica features que acaban de desbloquearse automáticamente
 */
export function getNewlyUnlockedFeatures(
  current: FamilyFeatures,
  previous: FamilyFeatures | null
): Array<keyof FamilyFeatures> {
  if (!previous) return [];
  
  const newlyUnlocked: Array<keyof FamilyFeatures> = [];
  
  for (const key in current) {
    const featureName = key as keyof FamilyFeatures;
    const curr = current[featureName];
    const prev = previous[featureName];
    
    // Se acaba de desbloquear Y no se ha mostrado notificación
    if (
      curr.unlocked && 
      !prev.unlocked && 
      curr.unlockedBy === 'auto' &&
      !curr.notificationShown
    ) {
      newlyUnlocked.push(featureName);
    }
  }
  
  return newlyUnlocked;
}

/**
 * Calcula progreso hacia desbloqueo (0-100)
 */
export function calculateProgress(metrics: FeatureMetrics): Record<string, number> {
  return {
    advancedTasks: Math.min(100, (
      (metrics.tasksCreated / 5) * 40 +
      (metrics.tasksCompleted / 10) * 40 +
      (metrics.daysActive / 3) * 20
    )),
    
    advancedActivities: Math.min(100, (
      (metrics.activitiesCreated / 3) * 60 +
      (metrics.daysActive / 5) * 40
    )),
    
    templateLibrary: Math.min(100, (
      ((metrics.tasksCreated + metrics.activitiesCreated) / 5) * 60 +
      (metrics.daysActive / 7) * 40
    )),
    
    calendar: Math.min(100, (metrics.daysActive / 14) * 100),
    
    groupGoals: Math.min(100, (
      (metrics.membersCount / 3) * 30 +
      (metrics.totalCompletions / 50) * 40 +
      (metrics.daysActive / 10) * 30
    )),
    
    advancedRecipes: Math.min(100, (
      (metrics.recipesCreated / 5) * 40 +
      (metrics.recipeDaysUsed / 7) * 30 +
      (metrics.daysActive / 14) * 30
    )),
    
    mealPlanCalendar: Math.min(100, (metrics.recipesCreated / 5) * 100),
    
    gritiAI: Math.min(100, (
      (metrics.daysActive / 7) * 40 +
      (metrics.totalCompletions / 20) * 40 +
      (metrics.hasExploredSections ? 20 : 0)
    ))
  };
}
